import { createMarker, debugLog, markers, measures, measureTimeBetweenMarks, profile, profileEnd } from 'debug';
import articleConfig from '~/config/article';
import { ENV } from '~/config/article/env';
import AdvertisementCreator from '~/modules/Advertisement';
import ServerLogger, { IRequestParams } from '~/modules/ServerLogger';
import WidgetsCreator from '~/modules/WidgetsCreator';
import { advancedKeys, blockedKeys } from '~/config/keys';
import EndlessMode from '~/modules/EndlessMode';
import { insertDummyFunctions, loadLegacyPlatform } from 'platform-startup';
import '~/modules/SSOLogin';

insertDummyFunctions();

/**
 * @file Main entry file that contains initialization
 */
if (typeof ''.matchAll === 'undefined' && window.VUUKLE_LEGACY_PLATFORM !== 1) {
  loadLegacyPlatform();
  window.VUUKLE_LEGACY_PLATFORM = 1;
  throw new Error('⚠️[VUUKLE] Old browser detected, loading legacy version of platform.js');
}

/**
 * Check if platform.js accidentally wasn't loaded twice on the same page.
 * If it's loaded twice then throw exception and stop execution.
 */
if (window.VUUKLE_PLATFORM === 1) {
  throw new Error('⚠️[VUUKLE] Vuukle is already defined.');
}

// 💾 Save that platform has been initialized, so the checking ⬆ above in next platform.js will throw error
window.VUUKLE_PLATFORM = 1;

/**
 * Import the scheduler polyfill if not supported
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Prioritized_Task_Scheduling_API
 */
if (!('scheduler' in window)) {
  import(
    /* webpackChunkName: "sch" */
    /* webpackPreload: true */
    'scheduler-polyfill'
  );
}

// ================================== APP ===============================================
profile('Platform');
createMarker(markers.platformStart);
// import WebVitalsReporter from '~/modules/WebVitalsReporter';
// import EmailTargetPixel from '~/modules/EmailTargetPixel';
// import { IVTReporter } from '~/modules/IVTReporter/IVTReporter';

/** Simulate error to prevent Vuukle initialization for the API keys in the blocked keys list */
if (blockedKeys.indexOf(articleConfig.apiKey) > -1) {
  throw new Error('Your domain has been blocked, please contact support[at]vuukle.com to get it unblocked');
}

/** Set cache bypass cookie for dev */
if (ENV.IS_STAGING) {
  document.cookie = 'cdntest=true; SameSite=Lax;';
}

// WebVitalsReporter.startVitalsReporters();
// if (Session.isBot) {
//   ServerLogger.reportBot(articleConfig);
// }

// let ivtReporter: any = undefined;

// const canInsertIVTReporter = () =>
//   advancedKeys[articleConfig.apiKey] && !articleConfig.amp && !ivtScanExcludeKeys.includes(articleConfig.apiKey);
// !Session.isBot;

/**
 * Insert IVT detector script which will report bad traffic
 */
// if (!articleConfig.endlessMode && canInsertIVTReporter()) {
//   ivtReporter = new IVTReporter(articleConfig);
// }

/**
 *
 */
function initializePlatform() {
  /**
   * Init widgets instantly only and only if endlessMode isn't configured or disabled by publisher.
   * Otherwise publisher is responsible to create widgets using `window.newVuukleWidgets` method.
   */
  if (!articleConfig.endlessMode) {
    scheduler.postTask(async () => {
      try {
        const widgetsCreator = await WidgetsCreator.createWidgetsCreatorInstance('', articleConfig);
        new AdvertisementCreator('', articleConfig, widgetsCreator.communicationCreator, widgetsCreator);
      } catch (e) {
        debugLog('error', e.message);
      }
      // if (advancedKeys[articleConfig.apiKey] && advancedKeys[articleConfig.apiKey].blockIVT) {
      //   if (ivtReporter) {
      //     /** Listen for IVT code response, then generate ads */
      //     ivtReporter.addIVTListener((trafficType: number | undefined) => {
      //       if (!advancedKeys[articleConfig.apiKey].blockIVT.includes(trafficType)) {
      //         new AdvertisementCreator('', articleConfig, widgetsCreator.communicationCreator, realtimeAnalytics);
      //       }
      //     });
      // } else if (!advancedKeys[articleConfig.apiKey]?.blockIVT) {
      //   new AdvertisementCreator('', articleConfig, widgetsCreator.communicationCreator, realtimeAnalytics);
      // }
    });
  }

  // const linkage = searchParams.get('vk_l') === 'true';
  // const hashedEmail = searchParams.get('vk_em');

  // /** Fire email targeting pixel */
  // if (linkage && hashedEmail) {
  //   new EmailTargetPixel(hashedEmail);
  // }

  scheduler.postTask(
    () => {
      const adsDisabledWithBool = typeof articleConfig.ads === 'boolean' && !articleConfig.ads;
      const adsDisabledWithObject = typeof articleConfig.ads === 'object' && articleConfig.ads.enabled === false;
      /** If the publisher has disabled the ads from config, log it to the server */
      if (adsDisabledWithBool || adsDisabledWithObject) {
        ServerLogger.log('ads_false', articleConfig);
      }
    },
    { priority: 'background' }
  );

  if (articleConfig.endlessMode) {
    scheduler.postTask(() => {
      EndlessMode.initializeEndlessMode();
    });
  }
}

/**
 * Track the user closing the page. Unlike exit_5s, this event will also fire after the 5 seconds
 * and can fire during the first 5 seconds alongside exit_5s
 */
// function trackPageUnload() {
//   if (document.visibilityState === 'hidden') {
//     const bqData: { key: string; value: any }[] = [
//       {
//         // Send the amount of time the user spent since platform loaded in unix timestamp seconds
//         key: 'timeSpent',
//         value: Math.floor(Date.now() / 1000) - Session.startTimestamp,
//       },
//     ];
//     if (articleConfig.endlessMode) {
//       bqData.push({ key: 'article_id', value: encodeURI(articleIDs[document.location.href]) });
//     }
//     ServerLogger.logPost('page_unload', articleConfig, true, bqData);
//   }
// }

// window.addEventListener('visibilitychange', trackPageUnload);
if (advancedKeys[articleConfig.apiKey]?.track5s) {
  /** Track if the user will close the page for the next 5 seconds */
  function trackPageClose() {
    if (document.visibilityState === 'hidden') {
      // Use logPost with sendBeacon argument
      ServerLogger.logPost(
        'exit_5s',
        articleConfig,
        true,
        articleConfig.endlessMode
          ? [{ key: 'article_id', value: encodeURI(EndlessMode.articleIDs.get(document.location.href) || '') }]
          : []
      );
    }
  }
  window.addEventListener('visibilitychange', trackPageClose);
  setTimeout(() => window.removeEventListener('visibilitychange', trackPageClose), 5000);
}

/**
 * Remove created widgets by elementIndex
 * @param {string} ID - elementIndex of the created widgets
 */
window.removeVuukleWidgets = (ID: string): void => {
  WidgetsCreator.removeWidgets('-' + ID);
};

/**
 * Run the advertisement creator manually
 * @param {string} ID - elementIndex of the created widgets
 */
window.generateVuukleAds = (ID?: string) => {
  new AdvertisementCreator(ID ? '-' + ID : '', articleConfig);
};

/* ========================================================
  * and one more... 😃 Log Platform version and support email
  ======================================================== */
// tslint:disable-next-line
console.log(
  `%c[VUUKLE] platform.js version: ${ENV.VERSION}. Need help? Reach us at support[at]vuukle[dot]com`,
  'color:#039BE5;'
);

// Workaround for endless scroll widget generation not working consistently
if ((window as any).tempVuukleWidgets) {
  window.newVuukleWidgets((window as any).tempVuukleWidgets);
  (window as any).tempVuukleWidgets = undefined;
}

/**
 * Add document level click listener which will track outbound links and record a view_exit if the user has clicked on it
 */
document.addEventListener('click', (event) => {
  let link: any = event.target;
  while (link && (typeof link.tagName == 'undefined' || link.tagName.toLowerCase() != 'a' || !link.href)) {
    link = link.parentNode;
  }

  if (link && link.href && link.host && link.host !== location.host) {
    const bqDataOverrides: { key: keyof IRequestParams; value: string }[] = [{ key: 'refDomain', value: link.host }];
    if (articleConfig.endlessMode) {
      bqDataOverrides.push({
        key: 'article_id',
        value: encodeURI(EndlessMode.articleIDs.get(document.location.href) || ''),
      });
    }

    if (!event.defaultPrevented) {
      ServerLogger.log('view_exit', articleConfig, bqDataOverrides);
      // Allow event to be sent before the page is unloaded
      if (!link.target || link.target.match(/^_(self|parent|top)$/i)) {
        setTimeout(() => {
          location.href = link.href;
        }, 150);
        event.preventDefault();
      }
    }
  }
});

profileEnd('Platform');
measureTimeBetweenMarks(measures.platformStartToLog, markers.platformStart, undefined);

/**
 *
 * @param cb
 */
function checkForSchedulerAndPerformActionWhenItsPresent(cb: () => void) {
  if ('scheduler' in window) {
    cb();
  } else {
    const interval = setInterval(() => {
      if ('scheduler' in window) {
        clearInterval(interval);
        cb();
      }
    }, 50);
  }
}

if (advancedKeys[articleConfig.apiKey]?.initAfterScroll) {
  /** Start platform activities once user scrolls the page */
  function initAfterScroll() {
    if (advancedKeys[articleConfig.apiKey]?.initAfterScrollPixels) {
      if (window.scrollY >= advancedKeys[articleConfig.apiKey]?.initAfterScrollPixels) {
        checkForSchedulerAndPerformActionWhenItsPresent(() => {
          scheduler.postTask(() => {
            initializePlatform();
          });
        });
        window.removeEventListener('scroll', initAfterScroll);
      }
    } else {
      checkForSchedulerAndPerformActionWhenItsPresent(() => {
        scheduler.postTask(() => {
          initializePlatform();
        });
      });
      window.removeEventListener('scroll', initAfterScroll);
    }
  }
  window.addEventListener('scroll', initAfterScroll);
} else {
  /** Start platform activities once DOM loads */
  if (document.readyState !== 'loading') {
    checkForSchedulerAndPerformActionWhenItsPresent(() => {
      scheduler.postTask(() => {
        initializePlatform();
      });
    });
  } else {
    let isInitialized = false;
    document.addEventListener('readystatechange', () => {
      if ((document.readyState === 'interactive' || document.readyState === 'complete') && !isInitialized) {
        checkForSchedulerAndPerformActionWhenItsPresent(() => {
          isInitialized = true;
          scheduler.postTask(() => {
            initializePlatform();
          });
        });
      }
    });
  }
}
